import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Videocam as VideocamIcon,
  Pool as PoolIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  MailOutline as MailOutlineIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useMenuPolicy } from '../brand/MenuPolicyContext';
import { MainNavbar } from '../components/common';
import { useOperatorMessages } from '../contexts/OperatorMessageContext';
import Badge from '@mui/material/Badge';

const drawerWidth = 240;

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { allowShellLink } = useMenuPolicy();
  const { unreadCount } = useOperatorMessages();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: '중앙 제어', icon: <DashboardIcon />, path: '/central-control', menuId: 'nav.dashboard' },
    { text: '개별 관제', icon: <VideocamIcon />, path: '/individual-monitoring', menuId: 'nav.individual' },
    {
      text: '관제 메시지',
      icon: (
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <MailOutlineIcon />
        </Badge>
      ),
      path: '/operator-messages',
      menuId: 'nav.messages',
    },
    { text: '시설 관리', icon: <PoolIcon />, path: '/pool-management', menuId: 'nav.pools' },
    { text: '알림 센터', icon: <NotificationsIcon />, path: '/alerts', menuId: null },
    { text: '시스템 설정', icon: <SettingsIcon />, path: '/central-control?tab=system', menuId: 'nav.system' },
  ].filter((item) => allowShellLink(item.menuId));

  const drawer = (
    <Box>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          시설 안전 관제 시스템
        </Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => isMobile && setMobileOpen(false)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // 현재 페이지 제목 가져오기
  const currentPageTitle = navItems.find((item) => item.path === location.pathname)?.text || '시설 안전 관제 시스템';

  return (
    <Box sx={{ display: 'flex' }}>
      {/* MainNavbar 컴포넌트로 대체 */}
      <MainNavbar
        onMenuClick={handleDrawerToggle}
        title={currentPageTitle}
        drawerWidth={drawerWidth}
      />
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: theme => theme.palette.background.default,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout; 